import React, { Fragment, useMemo, useState } from "react";

import { Popover, Transition } from "@headlessui/react";

import SearchInput from "./SearchInput";

export type EmphasizedElement<T> = {
  title?: React.ReactNode;
  elements: T[];
};

type PopoverPosition = "top" | "bottom" | "center";
type PopoverAlign = "left" | "right";
type Position = `${PopoverPosition}-${PopoverAlign}`;

type SearchPopoverProps<T extends { id: string }> = {
  items: T[];
  emphasized?: EmphasizedElement<T>[];
  title: React.ReactNode;
  open?: boolean;
  renderItem: (item: T, emphasized: boolean) => React.ReactNode;
  loading: boolean;
  noMatch?: React.ReactNode;
  hasSearch?: boolean;
  align?: Position;
  onSearchChange?: (value: string) => void;
  searchValue?: string;
  onItemSelected: (item: T, emphasized: boolean) => void;
  button?: React.ReactNode;
  placeholder?: string;
  footer?: React.ReactNode | React.ReactNode[];
};

function SearchPopover<T extends { id: string }>({
  title,
  items,
  open: controlledOpen,
  emphasized = [],
  placeholder,
  renderItem,
  noMatch,
  hasSearch = true,
  onSearchChange,
  searchValue,
  onItemSelected,
  align = "bottom-left",
  button,
  loading,
  footer,
}: SearchPopoverProps<T>) {
  const position = useMemo(() => {
    // prettier-ignore
    switch (align) {
      case "bottom-left": return "top-full mt-3";
      case "bottom-right": return "top-full right-0 mt-3";
      case "center-left": return "inset-y-1/2 mt-3";
      case "center-right": return "inset-y-1/2 right-0 mt-3";
      case "top-left": return "bottom-full mt-3";
      case "top-right": return "bottom-full right-0 mt-3";
    }
  }, [align]);

  return (
    <Popover className="relative">
      {({ open }) => (
        <>
          <Popover.Button as={Fragment}>{button}</Popover.Button>
          <Transition
            show={controlledOpen ?? open}
            as={React.Fragment}
            enter="transition ease-out duration-200"
            enterFrom="opacity-0 translate-y-1"
            enterTo="opacity-100 translate-y-0"
            leave="transition ease-in duration-150"
            leaveFrom="opacity-100 translate-y-0"
            leaveTo="opacity-0 translate-y-1"
          >
            <Popover.Panel
              static
              className={`absolute z-30 w-screen transform bg-white max-w-xxs sm:px-0 lg:max-w-xs ${position}`}
            >
              <div className="overflow-hidden divide-y rounded-lg shadow-lg ring-1 ring-black ring-opacity-5 divide-light-500">
                {hasSearch && onSearchChange && (
                  <div className="flex p-4 bg-white lg:grid-cols-2">
                    <SearchInput
                      liveSearch
                      onChange={onSearchChange}
                      value={searchValue}
                      placeholder={placeholder}
                      className="w-full"
                    />
                  </div>
                )}
                <div className="overflow-y-auto divide-y divide-light-500 max-h-96">
                  {emphasized.map(
                    ({ title: emphasizedTitle, elements }, index) =>
                      elements.length > 0 && (
                        <ul
                          key={index}
                          className="overflow-y-auto divide-y divide-light-500"
                          role="listbox"
                        >
                          {emphasizedTitle && (
                            <li
                              role="title"
                            >
                              {emphasizedTitle}
                            </li>
                          )}
                          {elements.map((item) => (
                            <li
                              key={item.id}
                              className="hover:bg-light-0"
                              onClick={() => onItemSelected(item, true)}
                              role="option"
                            >
                              {renderItem(item, true)}
                            </li>
                          ))}
                        </ul>
                      )
                  )}
                  {
                    items.length > 0 && (
                      <>
                        {title && (
                          <li
                            className="list-none"
                            role="title"
                          >
                            {title}
                          </li>
                        )}
                        <ul
                          className="overflow-y-auto divide-y divide-light-500"
                          role="listbox"
                        >
                          {items.map((item) => (
                            <li
                              key={item.id}
                              className=" hover:bg-light-0"
                              onClick={() => onItemSelected(item, false)}
                              role="option"
                            >
                              {renderItem(item, false)}
                            </li>
                          ))}
                        </ul>
                      </>
                    )
                  }
                  {!loading && items.length === 0 && noMatch}
                  {footer ?? null}
                </div>
              </div>
            </Popover.Panel>
          </Transition>
        </>
      )}
    </Popover>
  );
}

export default React.memo(SearchPopover);
